import { zodTransformFirestoreTimestampToDate } from "../utils/zodUtils.mjs";
import { customerDateOfBirthSchema } from "../utils/dateOfBirth.mjs";
import { zodFlavorId, zodTransformSignupPlanSelection } from "./ids.mjs";
import { defineFirestoreCollection } from "./firebaseCollectionSchema.mjs";
import { PiiCategory, RedactStrategy, meta } from "./meta.mjs";
import { clinicalConsultationInterestSchema, clinicalInsuranceCategorySchema } from "./consultation/clinicalInsuranceCategory.mjs";
import { z as z$1 } from "zod";

// src/models/signupSession.ts
function normalizeStringList(value) {
	if (Array.isArray(value)) return value;
	return Object.entries(value).filter(([, entryValue]) => !!entryValue).map(([key]) => key);
}
function normalizeOptionalStringList(value) {
	if (value === void 0) return;
	if (value === null) return null;
	return normalizeStringList(value);
}
const assessmentChoiceFieldSchema = meta(z$1.union([z$1.array(z$1.string()), z$1.record(z$1.string(), z$1.unknown())]).nullable().optional().transform(normalizeOptionalStringList), {
	bqType: "stringified_map",
	pii: { category: "health" }
});
const selectedClassesFieldSchema = meta(z$1.union([z$1.array(z$1.string()), z$1.record(z$1.string(), z$1.unknown())]).nullable().optional().transform((value) => {
	if (!value) return [];
	return normalizeStringList(value);
}), { bqType: "stringified_map" });
const patientInformationSurveyDataSchema = z$1.object({
	firstName: meta(z$1.string().nullable().optional(), { pii: { category: "name" } }),
	lastName: meta(z$1.string().nullable().optional(), { pii: { category: "name" } }),
	mobilePhone: meta(z$1.string().nullable().optional(), { pii: { category: "phone" } }),
	stateOfResidence: meta(z$1.string().nullable().optional(), { pii: { category: "geo" } }),
	dateOfBirth: meta(customerDateOfBirthSchema.nullable().optional(), { pii: {
		category: "date",
		redact: "truncateYear"
	} }),
	hearAboutUs: meta(z$1.string().nullable().optional(), { pii: { category: "freeText" } }),
	hearAboutUsProviderName: meta(z$1.string().nullable().optional(), { pii: { category: "freeText" } }),
	textMessages: z$1.boolean().nullable().optional()
});
const patientInformationSurveySchema = z$1.object({
	data: patientInformationSurveyDataSchema,
	currentPageNo: z$1.number()
});
const userAssessmentSurveyDataSchema = z$1.object({
	gender: meta(z$1.string().nullable().optional(), { pii: { category: "medicalId" } }),
	cancerType_women: assessmentChoiceFieldSchema,
	cancerType_nonWomen: assessmentChoiceFieldSchema,
	cancerType_general: assessmentChoiceFieldSchema,
	cancerType_combined: assessmentChoiceFieldSchema,
	supportNeeds: assessmentChoiceFieldSchema,
	dailyActivityAbility: meta(z$1.string().nullable().optional(), { pii: { category: "health" } }),
	currentSymptoms: assessmentChoiceFieldSchema,
	pastHolisticPractices: assessmentChoiceFieldSchema
});
const userAssessmentSurveySchema = z$1.object({
	data: userAssessmentSurveyDataSchema,
	currentPageNo: z$1.number()
});
/** Signup flow step ids persisted for resume (matches frontend `Step` minus null). */
const signupStepSchema = z$1.enum([
	"get-started",
	"lead-question",
	"lead-motivation",
	"referral-selection",
	"patient-information",
	"insurance-category",
	"clinical-consultation-interest",
	"thank-you-for-creating-account",
	"create-account",
	"user-assessment",
	"sponsor-screen",
	"signup-completed-message",
	"almost-there",
	"plan-selection",
	"stripe-checkout",
	"class-selection",
	"thank-you-for-subscribing",
	"schedule-consultation",
	"complete"
]);
/** Firestore document body for signup_sessions (no document id). */
const signupSessionPayloadSchema = z$1.object({
	sessionId: z$1.string().nullable(),
	leadQuestionId: z$1.string().nullable(),
	selectedPlan: z$1.string().transform(zodTransformSignupPlanSelection),
	selectedClasses: selectedClassesFieldSchema,
	patientInformationSurvey: patientInformationSurveySchema,
	userAssessmentSurvey: userAssessmentSurveySchema,
	lastUpdated: z$1.any().transform(zodTransformFirestoreTimestampToDate).pipe(z$1.date()).optional(),
	patientInformationFilledIn: z$1.boolean(),
	currentStep: signupStepSchema.nullable().optional(),
	clinicalInsuranceCategory: clinicalInsuranceCategorySchema.nullable().optional(),
	clinicalConsultationInterest: clinicalConsultationInterestSchema.nullable().optional()
});
const signupSessionSchema = signupSessionPayloadSchema.extend({
	id: zodFlavorId(),
	consultationIncluded: z$1.boolean().optional(),
	consultationScheduled: z$1.boolean().optional()
});
/** @deprecated Use signupSessionPayloadSchema */
const signupStoreDataSchema = signupSessionPayloadSchema;
/** @deprecated Use patientInformationSurveySchema */
const surveyStorageSchema = patientInformationSurveySchema;
/** @deprecated Use userAssessmentSurveySchema */
const userAssessmentSurveyStorageSchema = userAssessmentSurveySchema;
function hasAnsweredAssessmentChoice(value) {
	return (value?.length ?? 0) > 0;
}
/** Any saved assessment answers — used to detect restorable signup_sessions progress. */
function hasPartialUserAssessmentProgress(data) {
	return !!data.gender || hasAnsweredAssessmentChoice(data.cancerType_women) || hasAnsweredAssessmentChoice(data.cancerType_nonWomen) || hasAnsweredAssessmentChoice(data.cancerType_general) || hasAnsweredAssessmentChoice(data.cancerType_combined) || hasAnsweredAssessmentChoice(data.supportNeeds) || !!data.dailyActivityAbility || hasAnsweredAssessmentChoice(data.currentSymptoms) || hasAnsweredAssessmentChoice(data.pastHolisticPractices);
}
/** Matches required questions in userAssessment.json (including gender-specific cancer type). */
function isUserAssessmentSurveyComplete(data) {
	if (!data.gender) return false;
	return (() => {
		switch (data.gender) {
			case "Woman": return hasAnsweredAssessmentChoice(data.cancerType_women);
			case "Man": return hasAnsweredAssessmentChoice(data.cancerType_nonWomen);
			case "Nonbinary or gender-diverse": return hasAnsweredAssessmentChoice(data.cancerType_general);
			default: return hasAnsweredAssessmentChoice(data.cancerType_combined);
		}
	})() && hasAnsweredAssessmentChoice(data.supportNeeds) && !!data.dailyActivityAbility && hasAnsweredAssessmentChoice(data.currentSymptoms) && hasAnsweredAssessmentChoice(data.pastHolisticPractices);
}
const SignupSessions = defineFirestoreCollection("signup_sessions", signupSessionSchema, void 0, {
	dwReplication: true,
	firestoreCollectionPath: "signup_sessions"
});

export { SignupSessions, hasPartialUserAssessmentProgress, isUserAssessmentSurveyComplete, patientInformationSurveySchema, signupSessionPayloadSchema, signupSessionSchema, signupStepSchema, signupStoreDataSchema, surveyStorageSchema, userAssessmentSurveySchema, userAssessmentSurveyStorageSchema };
//# sourceMappingURL=signupSession.mjs.map